var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, Fragment } from 'react';
import { DialogContent, Stack, Dialog, DialogTitle, Typography, IconButton, Tabs, Tab, } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { Table } from '../../../../components/Table';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setUnit } from '../../../../redux/slices/attachments';
import { generateName } from '../../../../utils/generateName';
import { useAnalyticsEventTracker } from '../../../../hooks/useAnalyticsEventTracker';
import './InfoDialog.scss';
var formatLastUpdated = function (value) {
    var date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
    if (isNaN(date.getTime()))
        return String(value);
    // Render in the user's own locale and local time zone (with the zone label).
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });
};
var generateRestockMailHref = function (partNumber, description) {
    var base = "mailto:feedback@jcb.com?subject=".concat(encodeURIComponent("Availability Request - ".concat(partNumber !== null && partNumber !== void 0 ? partNumber : '')));
    var body = "Hello,\n\nI am interested in the following part, but it is currently unavailable on the website.\n\nPart Number: ".concat(partNumber !== null && partNumber !== void 0 ? partNumber : '', "\n\nPart Description: ").concat(description !== null && description !== void 0 ? description : '', "\n\nPlease provide available information regarding:\n\n- Current availability\n- Expected restock date\n- Lead time\n- Alternative or replacement part(s)\n\nThank you for your assistance.");
    return "".concat(base, "&body=").concat(encodeURIComponent(body));
};
var stockColumns = [
    { Header: 'Warehouse Code', accessor: 'wareHouseCode' },
    { Header: 'Warehouse', accessor: 'wareHouseName' },
    { Header: 'In Stock', accessor: 'inStockQty' },
];
// (JITIS-4278 QA feedback):
var ALLOWED_WAREHOUSE_CODES = new Set([
    'AUS2',
    'AUS3',
    'AUS4',
    'AGB1',
    'AGB2',
    'ACA1',
]);
export var InfoDialog = function (_a) {
    var data = _a.data, lastUpdated = _a.lastUpdated, handleClose = _a.handleClose;
    var dispatch = useAppDispatch();
    var eventTracker = useAnalyticsEventTracker('Attachments Table').eventTracker;
    var unitSystem = localStorage.getItem('unitSystem');
    var unit = useAppSelector(function (state) { return state.attachments; }).unit;
    var _b = useState(0), tab = _b[0], setTab = _b[1];
    useEffect(function () {
        if (unitSystem) {
            dispatch(setUnit(unitSystem));
        }
    }, []);
    useEffect(function () {
        if (data === null || data === void 0 ? void 0 : data.partNumber)
            setTab(0);
    }, [data === null || data === void 0 ? void 0 : data.partNumber]);
    if (!data)
        return null;
    var generateInfoData = function () {
        return Object.entries(data[unit]).map(function (_a) {
            var key = _a[0], value = _a[1];
            return {
                value: value === null || value === void 0 ? void 0 : value.value,
                title: generateName(key),
                unit: value === null || value === void 0 ? void 0 : value.unit,
            };
        });
    };
    var infoData = __spreadArray([
        {
            title: 'Fitment Note',
            value: data === null || data === void 0 ? void 0 : data.fitmentNotes,
        },
        {
            title: 'Sales-Code',
            value: data === null || data === void 0 ? void 0 : data.salesCode,
        },
        {
            title: 'Flow Demand',
            value: data === null || data === void 0 ? void 0 : data.flowDemand,
        },
        {
            title: 'Multi-Function Controls',
            value: data.multiFunctionControlsRequired ? 'Required' : 'Not Required',
        },
        {
            title: 'Pick-Up',
            value: data === null || data === void 0 ? void 0 : data.pickUp,
        }
    ], generateInfoData(), true);
    var infoItems = infoData.map(function (item) {
        var _a;
        return (item === null || item === void 0 ? void 0 : item.value) ? (_jsxs(Fragment, { children: [_jsx(Typography, { fontWeight: "bold", mt: 1, sx: { fontSize: 'var(--info-header-size)' }, children: item.title }), _jsxs(Typography, { sx: { fontSize: 'var(--info-value-size)' }, children: ["\u2022 ", "".concat(item.value, " ").concat((_a = item.unit) !== null && _a !== void 0 ? _a : '')] })] }, item.title)) : undefined;
    });
    var stockItems = data.stock
        ? Object.values(data.stock).filter(function (item) {
            return ALLOWED_WAREHOUSE_CODES.has(item.wareHouseCode);
        })
        : [];
    var hasStock = stockItems.some(function (item) { return item.inStockQty > 0; });
    return (_jsx(Dialog, { className: "info-dialog", open: !!data, onClose: handleClose, transitionDuration: { exit: 0 }, children: _jsxs("div", { className: "fitment-dialog", "data-testid": "fitment-dialog", children: [_jsxs("div", { className: "header", children: [_jsx(DialogTitle, { fontWeight: "bold", sx: { padding: 0 }, children: "More info" }), _jsx(IconButton, { className: "icon-button", "aria-label": "close", onClick: handleClose, children: _jsx(FontAwesomeIcon, { icon: faXmark, color: "black", size: "xs" }) })] }), _jsx(Stack, { direction: "row", spacing: 1, justifyContent: "space-between", alignItems: "center", className: "fitment-dialog-bar", children: (data === null || data === void 0 ? void 0 : data.partNumber) ? (_jsx(Typography, { variant: "h5", sx: { marginBottom: 1 }, children: "Part: ".concat(data === null || data === void 0 ? void 0 : data.partNumber) })) : undefined }), _jsxs(Tabs, { value: tab, onChange: function (_, value) {
                        eventTracker(value === 1
                            ? 'Availability Tab clicked'
                            : 'Specifications Tab clicked');
                        setTab(value);
                    }, className: "fitment-dialog-tabs", children: [_jsx(Tab, { label: _jsx("span", { className: "tab-label", children: "Spec" }) }), _jsx(Tab, { label: _jsx("span", { className: "tab-label", children: "Stock" }) })] }), _jsxs(DialogContent, { className: "fitment-dialog-content", children: [tab === 0 && infoItems, tab === 1 &&
                            (hasStock ? (_jsxs(_Fragment, { children: [lastUpdated ? (_jsxs(Typography, { mb: 1, sx: { fontSize: 'var(--info-value-size)' }, children: ["Stock levels accurate as of", _jsx(FontAwesomeIcon, { className: "stock-date-icon", icon: faCalendarDays }), formatLastUpdated(lastUpdated), " and liable to change. Please confirm stock before ordering."] })) : undefined, _jsx("div", { className: "stock-table-wrapper", children: _jsx(Table, { columns: stockColumns, data: stockItems }) })] })) : (_jsxs("div", { className: "restock-cta", children: [_jsxs(Typography, { sx: { fontSize: 'var(--info-value-size)' }, children: [_jsx("strong", { children: "Out of stock doesn't always mean unavailable." }), _jsx("br", {}), "Contact the JCB team\u2014click here for incoming inventory timing."] }), _jsx("a", { className: "restock-cta-button", href: generateRestockMailHref(data.partNumber, data.description), onClick: function () {
                                            return eventTracker('Confirm Availability Button clicked');
                                        }, children: "Confirm Availability" })] })))] })] }) }));
};
